import React, { ReactElement, useEffect, useState } from 'react';
import classNames from 'classnames';
import { BodyS, BodySBold } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import CustomSelect from 'components/CustomSelect/CustomSelect';
import Arrow from '@material-ui/icons/ArrowDropDown';
import { OutlinedInput, useTheme } from '@material-ui/core';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import TextField from '@material-ui/core/TextField';
import ChevronRight from '@material-ui/icons/ChevronRight';
import CanPerform from 'components/CanPerform/CanPerform';
import Delete from '@material-ui/icons/DeleteOutlined';
import DownloadMenu from 'components/DownloadMenu/DownloadMenu';
import Create from '@material-ui/icons/Create';
import ConfirmationDialog from 'components/ConfirmationDialog/ConfirmationDialog';
import { makeStyles } from '@material-ui/core/styles';
import styles from 'assets/jss/material-dashboard-pro-react/components/customTableStyle';
import typography from 'assets/jss/material-dashboard-pro-react/components/typographyStyle';
import ConfirmationDialogBody from 'components/CustomTable/ConfirmationDialogBody';
import Tooltip from '@material-ui/core/Tooltip';
import useMediaQuery from '@material-ui/core/useMediaQuery/useMediaQuery';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import TableInfoRow from 'views/PrepaidLoyalty/ManageGroups/subcomponents/TableInfoRow';
import { useSelector } from 'react-redux';
import { getAccessibilityLabels } from 'redux/modules/dynamicLabels/selectors';

// @ts-ignore
const useStyles = makeStyles(styles);
// @ts-ignore
const useTypography = makeStyles(typography);

interface IDownloadMenuItem {
	name: string;
	onClick: VoidFunction;
}

interface IPagination {
	pageSize: number;
	pages: number;
	pageSizeOptions: number[];
	onPageSizeChange: (e: any) => void;
	page: number;
	canNext: boolean;
	canPrevious: boolean;
	onPageChange: (e: any) => void;
	total: number;
	search: React.ReactElement | string;
	withHeader: boolean;
	additionalNote?: string;
	selectPanel: {
		customItems?: React.ReactElement;
		confirmationNotRequired?: boolean;
		confirmationDialogTitle: string;
		confirmationDialogBody: string | React.ReactElement;
		confirmationDialogButtonDeleteHidden?: boolean;
		selectedElements: number;
		downloadMenuItems: IDownloadMenuItem[];
		deleteItems?: VoidFunction;
		deleteItemsAction: string;
		editItems: VoidFunction;
		disableDelete?: boolean;
		disableDeleteText?: string;
		body?: ReactElement; // to customize select panel, we pass out own component
	};
}

const PaginationComponent = (props: IPagination) => {
	const classes = useStyles();
	const typography = useTypography();
	const [confirmationDialog, setConfirmationDialog] = useState(false);
	const {
		pageSize,
		pageSizeOptions,
		onPageSizeChange,
		onPageChange,
		pages,
		page,
		canNext,
		canPrevious,
		total,
		withHeader,
		additionalNote,
		selectPanel,
	} = props;
	const theme = useTheme();
	const smUp = useMediaQuery(theme.breakpoints.up('sm'));
	const accessibilityLabels = useSelector(getAccessibilityLabels);

	const [newPageSizeOptions, setNewPageSizeOptions] = useState(pageSizeOptions);

	useEffect(() => {
		const maxPageSize = pageSizeOptions.find((pso) => pso === total || pso > total);
		if (maxPageSize) setNewPageSizeOptions(pageSizeOptions.filter((pso) => pso <= maxPageSize));
	}, [pageSizeOptions]);

	const pageSizes = newPageSizeOptions.map((size) => ({
		value: size,
		label: size,
	}));

	const [isEmpty, setIsEmpty] = React.useState(false);

	const onChange = ({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
		if (value === '') {
			setIsEmpty(true);
		} else {
			setIsEmpty(false);
			if (!/[0-9]+/.test(value) || Number(value) === 0 || Number(value) > pages) {
				onPageChange(0);
			} else {
				onPageChange(Number(value) - 1);
			}
		}
	};

	const leftChevronClasses = classNames({
		[classes.chevron]: true,
		[classes.chevronEnabled]: canPrevious,
		[classes.chevronDisabled]: !canPrevious,
	});

	const rightChevronClasses = classNames({
		[classes.chevron]: true,
		[classes.chevronEnabled]: canNext,
		[classes.chevronDisabled]: !canNext,
	});

	const value = pages === 0 ? 0 : isEmpty ? '' : page + 1;
	const deleteIcon = (
		<Delete
			className={classNames({
				[classes.deleteIconDisabled]: !!selectPanel?.disableDelete,
			})}
		/>
	);
	return (
		<div
			className={classNames({
				[classes.paginationContainerWrapper]: true,
				[classes.paginationContainerWrapperWithHeader]: withHeader,
			})}
		>
			<div className={classes.paginationContainer}>
				<div className={classes.pageSizeContainer}>
					<span className={classes.results}>
						<BodySBold inline>{translate('table.results', String(total))}</BodySBold>
					</span>
					<div className={classes.show}>
						<BodySBold inline>{translate('table.show')}</BodySBold>
					</div>
					<CustomSelect
						items={pageSizes}
						value={pageSize}
						onChange={(event: any) => onPageSizeChange(event.target.value)}
						selectProps={{
							IconComponent: Arrow,
							input: (
								<OutlinedInput
									classes={{
										root: classes.pageCounterPage,
										input: classNames(classes.pageCounterPageInput, typography.bodyS),
										focused: classes.pageCounterPageInputFocus,
										notchedOutline: classes.pageCounterNotchedOutline,
									}}
								/>
							),
							variant: 'outlined',
							inputProps: { 'aria-label': accessibilityLabels?.paginationPerPage },
						}}
						className={classNames(classes.customSelectWrapper, classes.select)}
						disableUnderline
						menuClasses={classes.selectMenu}
					/>
				</div>
				<div className={classes.pageCounterContainer}>
					<ChevronLeft className={leftChevronClasses} onClick={() => canPrevious && onPageChange(page - 1)} />
					<TextField
						InputProps={{
							classes: {
								root: classes.textFieldRoom,
								input: classNames(classes.pageCounterPageInput, typography.bodyS),
								focused: classes.pageCounterPageInputFocus,
								notchedOutline: classes.pageCounterNotchedOutline,
							},
						}}
						value={value}
						onChange={onChange}
						variant="outlined"
						inputProps={{ 'aria-label': accessibilityLabels?.paginationPageNumber }}
					/>
					<BodyS color="secondary-light" disableMargin>
						{translate('table.pagesOf')}
					</BodyS>
					<div className={classes.pageCounterNumber}>
						<BodyS color="brand" disableMargin>
							{String(pages)}
						</BodyS>
					</div>
					<ChevronRight className={rightChevronClasses} onClick={() => canNext && onPageChange(page + 1)} />
				</div>
			</div>
			<ConditionalRender
				show={selectPanel && selectPanel.selectedElements > 0}
				onTrue={() => (
					<>
						{selectPanel.body ? (
							selectPanel.body
						) : (
							<>
								<div className={classes.selectPanel}>
									<ConditionalRender
										show={smUp}
										onTrue={() => (
											<BodyS color="primary-dark" disableMargin>
												{selectPanel.selectedElements === 1
													? translate('item.selected')
													: translate('items.selected', selectPanel.selectedElements)}
											</BodyS>
										)}
										onFalse={() => (
											<BodyS color="primary-dark" disableMargin>
												{String(selectPanel.selectedElements)}
											</BodyS>
										)}
									/>
									<div className={classes.selectPanelIconWrapper}>
										<ConditionalRender
											show={!!selectPanel?.customItems}
											onTrue={() => selectPanel.customItems}
										/>
										<CanPerform
											action={selectPanel.deleteItemsAction}
											yes={
												selectPanel.deleteItems && (
													<div
														className={classNames(classes.deleteIcon, 'delete-items-icon')}
														onClick={() => {
															if (
																!selectPanel.disableDelete &&
																!selectPanel.confirmationNotRequired
															) {
																setConfirmationDialog(true);
															} else if (
																!selectPanel.disableDelete &&
																selectPanel.confirmationNotRequired
															) {
																selectPanel.deleteItems && selectPanel.deleteItems();
															}
														}}
													>
														{selectPanel.disableDeleteText && selectPanel.disableDelete ? (
															<Tooltip title={selectPanel!.disableDeleteText!}>
																{deleteIcon}
															</Tooltip>
														) : (
															deleteIcon
														)}
													</div>
												)
											}
										/>
										<ConditionalRender
											show={!!selectPanel.downloadMenuItems}
											onTrue={() => <DownloadMenu items={selectPanel.downloadMenuItems} />}
										/>
										<ConditionalRender
											show={!!selectPanel.editItems}
											onTrue={() => (
												<div
													className={classes.deleteIcon}
													onClick={() => selectPanel.editItems()}
												>
													<Create />
												</div>
											)}
										/>
										<ConfirmationDialog
											isOpen={confirmationDialog}
											hideDialog={() => setConfirmationDialog(false)}
											header={
												selectPanel.confirmationDialogTitle ||
												translate('table.delete.elements')
											}
											dialogBody={
												<ConfirmationDialogBody
													confirmationDialogBody={selectPanel.confirmationDialogBody}
												/>
											}
											buttons={
												selectPanel?.confirmationDialogButtonDeleteHidden
													? [
															{
																text: translate('cancel'),
																onClick: () => setConfirmationDialog(false),
																color: 'tertiary-light',
															},
													  ]
													: [
															{
																text: translate('cancel'),
																onClick: () => setConfirmationDialog(false),
																color: 'tertiary-light',
															},

															{
																text: translate('delete', selectPanel.selectedElements),
																onClick: () => {
																	setConfirmationDialog(false);
																	selectPanel?.deleteItems &&
																		selectPanel.deleteItems();
																},
																color: 'primary-light',
															},
													  ]
											}
										/>
									</div>
									<div className={classes.selectPanelArrow} />
								</div>
							</>
						)}
					</>
				)}
			/>
			<ConditionalRender
				show={!!additionalNote}
				onTrue={() => <TableInfoRow text={additionalNote ? additionalNote : ''} />}
			/>
		</div>
	);
};

export default PaginationComponent;
